import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { canViewPrices } from '../../utils/pricing'; // إخفاء عمود السعر عن الأدوار غير 1/3/7
import {
  SMART_COLORS as COLORS,
  smartActionButtonSx,
  smartCardSx as cardSx,
  smartTableContainerSx,
  smartTableHeadSx,
  smartTableSx,
} from '../../theme/smartStableTheme';
import SettingsPageHeader from '../../components/SettingsPageHeader';
import SmartTablePagination, { useSmartPagination } from '../../components/SmartTablePagination';

const apiUrl = process.env.REACT_APP_API_URL;

const ProceduresList = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [procedures, setProcedures] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [search, setSearch] = useState('');
  const [showZeroOnly, setShowZeroOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isRtl = String(i18n.language || '').startsWith('ar');

  const fetchProcedures = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`${apiUrl}/procedures`);
      setProcedures(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || t('proceduresList.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchProcedures();
  }, [fetchProcedures]);

  // تنبيه بيانات صفريّة: إجراء بلا سعر (احتاج طاقم العيادة تعبئته قبل الاستخدام).
  const isZeroRecord = useCallback((procedure) => Number(procedure?.price) <= 0, []);
  const zeroProceduresCount = useMemo(
    () => procedures.filter(isZeroRecord).length,
    [procedures, isZeroRecord]
  );

  const filteredProcedures = useMemo(() => {
    const term = search.trim().toLowerCase();
    let list = procedures;
    if (showZeroOnly) list = list.filter(isZeroRecord);
    if (!term) return list;
    return list.filter((procedure) => (
      String(procedure.procedure_name || '').toLowerCase().includes(term)
      || String(procedure.description || '').toLowerCase().includes(term)
    ));
  }, [procedures, search, showZeroOnly, isZeroRecord]);

  const {
    page,
    rowsPerPage,
    paginatedItems: paginatedProcedures,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useSmartPagination(filteredProcedures, [search]);

  const handleDelete = useCallback(async () => {
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await api.delete(`${apiUrl}/procedures/${deleteId}`);
      setDeleteId(null);
      setMessage(t('proceduresList.deleteSuccess'));
      await fetchProcedures();
    } catch (err) {
      setError(err?.response?.data?.error || t('proceduresList.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [deleteId, fetchProcedures, t]);

  return (
    <Box className="smart-page-container" dir={isRtl ? 'rtl' : 'ltr'} sx={{ bgcolor: COLORS.page, minHeight: '100vh', maxWidth: 'var(--app-page-max)', mx: 'auto' }}>
      <SettingsPageHeader
        title={t('proceduresList.title')}
        subtitle={t('proceduresList.pageHint')}
        count={procedures.length}
        actions={(
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/procedure-add')} sx={{ bgcolor: COLORS.gold, '&:hover': { bgcolor: COLORS.goldHover }, fontWeight: 900 }}>
            {t('proceduresList.addNew')}
          </Button>
        )}
      />

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
      {zeroProceduresCount > 0 && (
        <Alert
          severity="warning"
          sx={{ mb: 2, cursor: 'pointer' }}
          onClick={() => setShowZeroOnly((value) => !value)}
          action={showZeroOnly ? (
            <Button size="small" onClick={(event) => { event.stopPropagation(); setShowZeroOnly(false); }} sx={{ fontWeight: 900 }}>
              {t('proceduresList.clearZeroFilter')}
            </Button>
          ) : undefined}
        >
          {t(showZeroOnly ? 'proceduresList.zeroFilterActive' : 'proceduresList.zeroAlert', { count: zeroProceduresCount })}
        </Alert>
      )}

      <Paper elevation={0} sx={{ ...cardSx, overflow: 'hidden' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
          <TextField
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            label={t('proceduresList.search')}
            InputProps={{ startAdornment: <SearchIcon sx={{ color: COLORS.muted, mr: isRtl ? 0 : 1, ml: isRtl ? 1 : 0 }} /> }}
            sx={{ maxWidth: { sm: 420 } }}
            fullWidth
          />
        </Stack>
        <TableContainer sx={smartTableContainerSx}>
          <Table size="small" sx={smartTableSx}>
            <TableHead sx={smartTableHeadSx}>
              <TableRow>
                <TableCell align="center" sx={{ width: 72 }}>#</TableCell>
                <TableCell>{t('proceduresList.name')}</TableCell>
                <TableCell>{t('proceduresList.description')}</TableCell>
                {canViewPrices() && <TableCell align="center">{t('proceduresList.price')}</TableCell>}
                <TableCell align="center" sx={{ width: 150 }}>{t('proceduresList.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProcedures.map((procedure, index) => (
                <TableRow key={procedure.procedure_id} hover>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: COLORS.text, fontWeight: 900, minWidth: 180 }}>{procedure.procedure_name}</TableCell>
                  <TableCell sx={{ minWidth: 240 }}>{procedure.description || '-'}</TableCell>
                  {canViewPrices() && <TableCell align="center">{procedure.price ?? '-'}</TableCell>}
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('edit')}>
                        <IconButton onClick={() => navigate(`/ProcedureEdit/${procedure.procedure_id}`)} sx={smartActionButtonSx(COLORS.blue, COLORS.blueSoft)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('delete')}>
                        <IconButton onClick={() => setDeleteId(procedure.procedure_id)} sx={smartActionButtonSx(COLORS.rose, COLORS.roseSoft)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!filteredProcedures.length && !loading && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography sx={{ color: COLORS.muted, py: 3, textAlign: 'center' }}>
                      {search ? t('no_search_results') : t('proceduresList.noProcedures')}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <SmartTablePagination count={filteredProcedures.length} page={page} rowsPerPage={rowsPerPage} onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
      </Paper>

      <Dialog open={Boolean(deleteId)} onClose={() => setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: COLORS.text, fontWeight: 900 }}>{t('delete_confirmation')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('proceduresList.confirmDelete')}</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: COLORS.muted, fontWeight: 900 }}>{t('cancel')}</Button>
          <Button onClick={handleDelete} disabled={saving} sx={{ color: COLORS.rose, fontWeight: 900 }}>{t('delete')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProceduresList;
